/* Collection browse: filter panel, apply filters and sort, and append the next page. */
{
  const roots = document.querySelectorAll('[data-sc-collection]');
  const desktop = window.matchMedia('(min-width: 990px)');

  const bind = (root) => {
    if (root.dataset.scReady === 'true') return;
    root.dataset.scReady = 'true';

    const submit = () => {
      if (typeof root.requestSubmit === 'function') root.requestSubmit();
      else root.submit();
    };

    // The category row scrolls sideways on small screens; bring the current one into view.
    const cats = root.querySelector('[data-sc-cats]');
    const currentCat = cats?.querySelector('[aria-current="page"]');
    if (cats && currentCat && cats.scrollWidth > cats.clientWidth) {
      cats.scrollLeft = currentCat.offsetLeft - cats.offsetLeft - (cats.clientWidth - currentCat.offsetWidth) / 2;
    }

    const panel = root.querySelector('[data-sc-filter-panel]');
    const toggle = root.querySelector('[data-sc-filter-open]');
    const close = root.querySelector('[data-sc-filter-close]');

    // Below the desktop sidebar the panel is a full-screen sheet: changes wait for "Show products".
    const setOpen = (open) => {
      if (!panel || !toggle) return;
      panel.classList.toggle('is-open', open);
      document.documentElement.classList.toggle('sc-filters-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        close?.focus();
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
      }
    };

    toggle?.addEventListener('click', () => {
      setOpen(true);
    });

    close?.addEventListener('click', () => {
      setOpen(false);
      toggle?.focus();
    });

    panel?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !panel.classList.contains('is-open')) return;
      event.stopPropagation();
      setOpen(false);
      toggle?.focus();
    });

    desktop.addEventListener('change', () => {
      if (desktop.matches) setOpen(false);
    });

    const sortMenu = root.querySelector('[data-sc-sort-menu]');
    const sortTrigger = sortMenu?.querySelector('[data-sc-sort-trigger]');
    const sortList = sortMenu?.querySelector('[data-sc-sort-list]');
    const sortSelect = sortMenu?.querySelector('[data-sc-sort]');
    const sortOptions = sortList ? [...sortList.querySelectorAll('[role="option"]')] : [];

    const setActive = (option) => {
      for (const item of sortOptions) item.classList.toggle('is-active', item === option);
      if (option) {
        sortList.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    };

    const setSortOpen = (open) => {
      if (!sortList) return;
      sortList.hidden = !open;
      sortTrigger.setAttribute('aria-expanded', String(open));
      if (open) {
        setActive(sortOptions.find((item) => item.getAttribute('aria-selected') === 'true') ?? sortOptions[0]);
        sortList.focus();
      }
    };

    const choose = (option) => {
      setSortOpen(false);
      sortTrigger.focus();
      if (!option || option.dataset.value === sortSelect.value) return;
      for (const item of sortOptions) item.setAttribute('aria-selected', String(item === option));
      sortMenu.querySelector('[data-sc-sort-value]').textContent = option.textContent;
      sortSelect.value = option.dataset.value;
      sortSelect.dispatchEvent(new Event('change', { bubbles: true }));
    };

    sortTrigger?.addEventListener('click', () => {
      setSortOpen(sortList.hidden);
    });

    sortTrigger?.addEventListener('keydown', (event) => {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      setSortOpen(true);
    });

    sortList?.addEventListener('keydown', (event) => {
      const current = sortOptions.findIndex((item) => item.classList.contains('is-active'));
      const moves = { ArrowDown: current + 1, ArrowUp: current - 1, Home: 0, End: sortOptions.length - 1 };
      if (event.key in moves) {
        event.preventDefault();
        setActive(sortOptions[Math.min(Math.max(moves[event.key], 0), sortOptions.length - 1)]);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        choose(sortOptions[current]);
      } else if (event.key === 'Escape') {
        event.stopPropagation();
        setSortOpen(false);
        sortTrigger.focus();
      } else if (event.key === 'Tab') {
        setSortOpen(false);
      }
    });

    sortList?.addEventListener('click', (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) choose(option);
    });

    sortList?.addEventListener('pointermove', (event) => {
      const option = event.target.closest('[role="option"]');
      if (option && !option.classList.contains('is-active')) setActive(option);
    });

    document.addEventListener('click', (event) => {
      if (sortList && !sortList.hidden && !sortMenu.contains(event.target)) setSortOpen(false);
    });

    root.addEventListener('change', (event) => {
      const field = event.target;
      if (!(field instanceof HTMLElement) || !field.name) return;
      if (field.dataset.scChip) {
        for (const twin of root.querySelectorAll(`[data-sc-twin="${field.dataset.scChip}"]`)) twin.checked = field.checked;
      }
      if (field.matches('[data-sc-sort]') || desktop.matches) submit();
    });

    const more = root.querySelector('[data-sc-more]');
    const link = root.querySelector('[data-sc-load-more]');
    if (!more || !link) return;

    link.addEventListener('click', async (event) => {
      event.preventDefault();
      if (more.getAttribute('aria-busy') === 'true') return;
      more.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetch(url);
        if (!response.ok) throw new Error('load failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = root.querySelector('[data-sc-grid]');
        const cards = doc.querySelectorAll('[data-sc-grid] [data-sc-card]');
        if (!grid || !cards.length) throw new Error('missing products');
        grid.append(...cards);
        const nextLink = doc.querySelector('[data-sc-load-more]');
        if (nextLink?.href) link.href = nextLink.href;
        else more.remove();
      } catch {
        window.location.assign(link.href);
      } finally {
        if (more.isConnected) more.removeAttribute('aria-busy');
      }
    });
  };

  for (const root of roots) bind(root);
}
